import { Routes } from '@shared/constants/routes';
import { SidebarLayout } from '@shared/layouts';
import { Outlet, type RouteObject } from 'react-router';
import { Dialogs } from '@pages/private/dialogs';
import { Channels, CreateChannel, Integrations, UpdateChannel } from '@pages/private/channels';
import { AiAgent, AiAgents } from '@pages/private/ai-agents';
import { Analytics } from '@pages/private/analytics';
import { KnowledgeBase } from '@pages/private/knowledge-base';
import { Preview } from '@pages/private/preview';
import { Billing } from '@pages/private/billing';
import { Organization, OrganizationDetails } from '@pages/private/profile/organization';
import { Profile } from '@pages/private/profile/user';
import { Team } from '@pages/private/team';
import { PresetEditor, Presets } from '@pages/private/presets';
import { LegacyOrganizationRedirect } from '../routing/legacy-organization-redirect';
import { OrganizationRouteOutlet } from '../routing/organization-route-outlet';
import { PermissionRoute } from '../routing/permission-route';

export const privateRoutes: RouteObject[] = [
  {
    element: <SidebarLayout />,
    children: [
      // Organization
      {
        path: Routes.profile.organization.root,
        element: <Organization />,
      },
      {
        path: Routes.profile.organization.organizationDetails,
        element: <OrganizationDetails />,
      },
      {
        path: Routes.profile.user.root,
        element: <Profile />,
      },

      {
        path: Routes.team.root,
        element: <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.team} />,
      },

      {
        path: Routes.analytics.root,
        element: <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.analytics} />,
      },
      {
        path: Routes.dialogs.root,
        element: <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.dialogs} />,
      },
      {
        path: Routes.dialogs.dialog,
        element: <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.dialog} />,
      },
      {
        path: Routes.aiAgents.root,
        element: <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.aiAgents} />,
      },
      {
        path: Routes.aiAgents.create,
        element: (
          <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.aiAgentsCreate} />
        ),
      },
      {
        path: Routes.aiAgents.agent,
        element: <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.aiAgent} />,
      },
      {
        path: Routes.channels.root,
        element: <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.channels} />,
      },
      {
        path: Routes.channels.create,
        element: (
          <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.channelsCreate} />
        ),
      },
      {
        path: Routes.channels.update,
        element: (
          <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.channelsUpdate} />
        ),
      },
      {
        path: Routes.channels.integrations,
        element: (
          <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.channelsIntegrations} />
        ),
      },
      {
        path: Routes.presets.root,
        element: <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.presets} />,
      },
      {
        path: Routes.presets.create,
        element: (
          <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.presetsCreate} />
        ),
      },
      {
        path: Routes.presets.preset,
        element: <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.preset} />,
      },
      {
        path: Routes.knowledgeBase.root,
        element: (
          <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.knowledgeBase} />
        ),
      },
      {
        path: Routes.preview.root,
        element: <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.preview} />,
      },
      {
        path: Routes.billing.root,
        element: <LegacyOrganizationRedirect targetRoute={Routes.organizationScope.billing} />,
      },

      {
        path: Routes.organizationScope.root,
        element: <OrganizationRouteOutlet />,
        children: [
          {
            path: 'analytics',
            element: (
              <PermissionRoute requiredPermission="ADMIN">
                <Analytics />
              </PermissionRoute>
            ),
          },
          {
            path: 'dialogs',
            element: (
              <PermissionRoute requiredPermission="MANAGER">
                <Dialogs />
              </PermissionRoute>
            ),
          },
          {
            path: 'dialogs/dialog/:dialogId',
            element: (
              <PermissionRoute requiredPermission="MANAGER">
                <Dialogs />
              </PermissionRoute>
            ),
          },
          {
            path: 'ai-agents',
            element: (
              <PermissionRoute requiredPermission="ADMIN">
                <AiAgents />
              </PermissionRoute>
            ),
            children: [
              {
                path: 'create',
                element: <AiAgents />,
              },
              {
                path: 'agent/:agentId',
                element: <AiAgent />,
              },
            ],
          },
          {
            path: 'channels',
            element: (
              <PermissionRoute requiredPermission="ADMIN">
                <Outlet />
              </PermissionRoute>
            ),
            children: [
              { index: true, element: <Channels /> },
              { path: 'create/:type', element: <CreateChannel /> },
              { path: ':channelId', element: <UpdateChannel /> },
              { path: ':channelId/integrations', element: <Integrations /> },
            ],
          },
          {
            path: 'knowledge-base',
            element: (
              <PermissionRoute requiredPermission="ADMIN">
                <KnowledgeBase />
              </PermissionRoute>
            ),
          },
          {
            path: 'preview',
            element: (
              <PermissionRoute requiredPermission="ADMIN">
                <Preview />
              </PermissionRoute>
            ),
          },
          {
            path: 'presets',
            element: (
              <PermissionRoute requiredPermission="ADMIN">
                <Outlet />
              </PermissionRoute>
            ),
            children: [
              { index: true, element: <Presets /> },
              { path: 'create', element: <PresetEditor /> },
              { path: ':presetId', element: <PresetEditor /> },
            ],
          },
          {
            path: 'team',
            element: (
              <PermissionRoute requiredPermission="MANAGER">
                <Team />
              </PermissionRoute>
            ),
          },
          {
            path: 'billing',
            element: (
              <PermissionRoute requiredPermission="OWNER">
                <Billing />
              </PermissionRoute>
            ),
          },
        ],
      },
    ],
  },
];
